import { useQuery, useMutation } from "@tanstack/react-query";
import { Link } from "wouter";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Bell,
  Star,
  Shield,
  CalendarDays,
  Grid3X3,
  Check,
  CheckCheck,
  ArrowRight,
  AlertTriangle,
} from "lucide-react";
import type { Notification } from "@shared/schema";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";

const TYPE_ICONS: Record<string, React.ElementType> = {
  review: Star,
  listing: Shield,
  post: CalendarDays,
  geogrid: Grid3X3,
  system: Bell,
  checkout_abandoned: AlertTriangle,
  admin_checkout_abandoned: AlertTriangle,
};

const TYPE_COLORS: Record<string, string> = {
  review: "bg-amber-500/10 text-amber-500",
  listing: "bg-red-500/10 text-red-500",
  post: "bg-blue-500/10 text-blue-500",
  geogrid: "bg-emerald-500/10 text-emerald-500",
  system: "bg-muted text-muted-foreground",
  checkout_abandoned: "bg-orange-500/10 text-orange-500",
  admin_checkout_abandoned: "bg-orange-500/10 text-orange-500",
};

function NotificationCard({ notification }: { notification: Notification }) {
  const { toast } = useToast();
  const Icon = TYPE_ICONS[notification.type] || Bell;
  const colorClass = TYPE_COLORS[notification.type] || TYPE_COLORS.system;

  const markReadMutation = useMutation({
    mutationFn: async () => {
      await apiRequest("PATCH", `/api/notifications/${notification.id}/read`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/notifications"] });
    },
  });

  return (
    <Card
      className={notification.isRead ? "opacity-60" : ""}
      data-testid={`card-notification-${notification.id}`}
    >
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          <div className={`flex items-center justify-center w-9 h-9 rounded-md shrink-0 ${colorClass}`}>
            <Icon className="w-4 h-4" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-2">
              <h3 className="text-sm font-semibold">{notification.title}</h3>
              {!notification.isRead && (
                <Button
                  size="icon"
                  variant="ghost"
                  onClick={() => markReadMutation.mutate()}
                  data-testid={`button-mark-read-${notification.id}`}
                >
                  <Check className="w-3 h-3" />
                </Button>
              )}
            </div>
            <p className="text-sm text-muted-foreground mt-0.5">{notification.message}</p>
            {notification.createdAt && (
              <p className="text-xs text-muted-foreground mt-1.5">
                {new Date(notification.createdAt).toLocaleString()}
              </p>
            )}
            {notification.actionUrl && notification.actionLabel && (
              <div className="mt-3">
                <Button
                  asChild
                  size="sm"
                  variant="default"
                  data-testid={`button-action-${notification.id}`}
                  onClick={() => {
                    if (!notification.isRead) markReadMutation.mutate();
                  }}
                >
                  <Link href={notification.actionUrl}>
                    {notification.actionLabel}
                    <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
                  </Link>
                </Button>
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function NotificationsPage() {
  const { toast } = useToast();

  const { data: notifications = [], isLoading } = useQuery<Notification[]>({
    queryKey: ["/api/notifications"],
  });

  const markAllReadMutation = useMutation({
    mutationFn: async () => {
      await apiRequest("PATCH", "/api/notifications/read-all");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/notifications"] });
      toast({ title: "All notifications marked as read" });
    },
  });

  const unreadCount = notifications.filter((n) => !n.isRead).length;

  if (isLoading) {
    return (
      <div className="p-6 space-y-4">
        <Skeleton className="h-8 w-48" />
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} className="h-20" />
        ))}
      </div>
    );
  }

  return (
    <div className="p-6 pb-24 space-y-6 overflow-y-auto h-full">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight" data-testid="text-notifications-title">
            Notifications
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            {unreadCount > 0 ? `${unreadCount} unread notification${unreadCount > 1 ? "s" : ""}` : "All caught up"}
          </p>
        </div>
        {unreadCount > 0 && (
          <Button
            variant="secondary"
            size="sm"
            onClick={() => markAllReadMutation.mutate()}
            disabled={markAllReadMutation.isPending}
            data-testid="button-mark-all-read"
          >
            <CheckCheck className="w-4 h-4 mr-1" />
            Mark All Read
          </Button>
        )}
      </div>

      <div className="space-y-3">
        {notifications.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center py-12 text-center">
              <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mb-4">
                <Bell className="w-8 h-8 text-primary" />
              </div>
              <h3 className="text-lg font-semibold">No Notifications</h3>
              <p className="text-sm text-muted-foreground mt-2">
                You're all caught up. New alerts will appear here.
              </p>
            </CardContent>
          </Card>
        ) : (
          notifications.map((notification) => (
            <NotificationCard key={notification.id} notification={notification} />
          ))
        )}
      </div>
    </div>
  );
}
